import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import BeginnerDashboard from './BeginnerDashboard';
import EngagedDashboard from './EngagedDashboard';
import PowerUserDashboard from './PowerUserDashboard';
import { getAthleteProfile } from '../../api/auth';
import { getUserEngagementLevel, UserMetrics, EngagementLevel } from '../../utils/engagement';

const ProgressiveDashboard: React.FC = () => {
  const { user, isGuest } = useAuth();
  const [engagementLevel, setEngagementLevel] = useState<EngagementLevel>('beginner');
  const [userMetrics, setUserMetrics] = useState<UserMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      // Handle guest mode
      if (isGuest) {
        const guestMetrics: UserMetrics = {
          totalWorkouts: 0,
          currentStreak: 0,
          weeklyWorkouts: 0,
          accountAge: 0,
          lastWorkoutDate: null,
          hasCompletedFirstWorkout: false,
          hasSetGoals: false,
          hasConnectedWithCoach: false,
        };
        setUserMetrics(guestMetrics);
        setEngagementLevel('beginner');
        setLoading(false);
        return;
      }
      
      // Fetch user profile and metrics
      const profile = await getAthleteProfile();
      
      // TODO: Replace with actual API call to get user metrics
      // For now, using mock data
      const mockMetrics: UserMetrics = {
        totalWorkouts: 0,
        currentStreak: 0,
        weeklyWorkouts: 0,
        accountAge: calculateAccountAge(user?.created_at),
        lastWorkoutDate: null,
        hasCompletedFirstWorkout: false,
        hasSetGoals: !!profile.primary_goal,
        hasConnectedWithCoach: false,
      };
      
      setUserMetrics(mockMetrics);
      
      // Determine engagement level
      const level = getUserEngagementLevel(mockMetrics);
      setEngagementLevel(level);
      
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAccountAge = (createdAt?: string): number => {
    if (!createdAt) return 0;
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4B5FDC]"></div>
      </div>
    );
  }

  // Render appropriate dashboard based on engagement level
  switch (engagementLevel) {
    case 'power_user':
      return <PowerUserDashboard metrics={userMetrics} />;
    case 'engaged':
      return <EngagedDashboard />;
    case 'beginner':
    default:
      return <BeginnerDashboard />;
  }
};

export default ProgressiveDashboard;